import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { verifyEstimatorPassword, saveEstimate, getEstimate } from "@/lib/estimator.functions";
import { getStoredPassword, setStoredPassword, clearStoredPassword } from "@/lib/estimator-session";
import { z } from "zod";
import { NumberInputRaw } from "@/components/ui/number-input";
import jsPDF from "jspdf";
import logoPng from "@/assets/apex-av-new-logo.png";
import {
  Tv, Home, Music, Sun, Shield, Cpu, Lightbulb, Speaker, Thermometer,
  MonitorPlay, EyeOff, Blinds, HardHat, Cable, Monitor, Mic, Lock, ArrowRight, Download, ChevronDown, Save, FolderOpen,
  Camera, ImagePlus, X, Loader2,
} from "lucide-react";

type Photo = { id: string; dataUrl: string; caption: string; includeInPdf?: boolean };

async function compressImage(file: File, maxDim = 1400, quality = 0.72): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = dataUrl;
  });
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return dataUrl;
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", quality);
}

export const Route = createFileRoute("/estimator")({
  validateSearch: (search) =>
    z.object({ id: z.string().uuid().optional() }).parse(search),
  head: () => ({
    meta: [
      { title: "Project Budget Estimator | Apex AV" },
      { name: "description", content: "Private budget estimator for Apex AV clients." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: EstimatorPage,
});

type Tier = { label: string; price: number; note?: string };
type Component = { key: string; label: string; price: number };
type ServiceItem = {
  id: string;
  title: string;
  icon: typeof Tv;
  tiers: Tier[];
  hasQuantity?: boolean;
  unitLabel?: string;
  components?: Component[];
  upchargeLabel?: string;
};

const SERVICES: ServiceItem[] = [
  { id: "tv-mounting", title: "TV Mounting", icon: Monitor, hasQuantity: true, unitLabel: "TV",
    tiers: [
      { label: "Basic fixed mount (drywall, up to 65\")", price: 199 },
      { label: "Tilt mount (drywall / wood studs, any size)", price: 249 },
      { label: "Full-motion articulating mount", price: 399 },
      { label: "Soundbar + TV combo mount & wire conceal", price: 449 },
      { label: "Fireplace / brick / in-wall power routing", price: 599 },
      { label: "Outdoor TV wall mount (sealed hardware)", price: 699 },
      { label: "Large format 85\"+ with full concealment", price: 899 },
      { label: "Recessed / flush-in-wall mount (custom box)", price: 1299 },
      { label: "Motorized TV lift or flip-down mount", price: 2499 },
    ] },
  { id: "home-theater", title: "Custom Home Theater", icon: Tv,
    tiers: [
      { label: "Entry dedicated theater (4K, surround, screen)", price: 25000 },
      { label: "Premium Atmos build with acoustic treatment", price: 80000 },
      { label: "Reference-grade theater (9.4.6, full design)", price: 175000 },
    ] },
  { id: "smart-home", title: "Smart Home Automation", icon: Home,
    tiers: [
      { label: "Single-room universal remote (programmed, one room)", price: 899 },
      { label: "Single-room smart package (lighting + climate + shades)", price: 4500 },
      { label: "Starter automation (1–2 systems unified, 2–3 rooms)", price: 7500 },
      { label: "Multi-room control (lighting, climate, shades, 4–6 rooms)", price: 14000 },
      { label: "Whole-home automation hub & custom programming", price: 20000 },
      { label: "Advanced integration (AV, security, intercom, pools)", price: 35000 },
      { label: "Luxury integrated control (fully custom, whole home)", price: 50000 },
      { label: "Estate-level automation (multiple structures, guest houses)", price: 85000 },
      { label: "Concierge integration (full design + remote monitoring)", price: 125000 },
    ] },
  { id: "whole-home-audio", title: "Whole-Home Audio", icon: Music, hasQuantity: true, unitLabel: "zone",
    tiers: [
      { label: "Standard in-ceiling zone (pair + amp share)", price: 1800 },
      { label: "High-performance zone (premium speakers)", price: 3500 },
      { label: "Audiophile reference zone", price: 6500 },
    ] },
  { id: "hidden-audio", title: "Hidden & Invisible Audio", icon: EyeOff, hasQuantity: true, unitLabel: "room",
    tiers: [
      { label: "Invisible-series pair, plaster-over", price: 4500 },
      { label: "Premium invisible w/ subwoofer", price: 8500 },
    ] },
  { id: "whole-home-video", title: "Whole-Home Video Distribution", icon: MonitorPlay, hasQuantity: true, unitLabel: "TV",
    tiers: [
      { label: "4K HDR matrix per endpoint", price: 1500 },
      { label: "4K HDR + control integration", price: 2800 },
    ] },
  { id: "outdoor", title: "Outdoor Entertainment", icon: Sun,
    tiers: [
      { label: "Patio TV + landscape speakers", price: 8500 },
      { label: "Full outdoor system (TV, audio, lighting)", price: 22000 },
      { label: "Resort-style outdoor entertainment", price: 60000 },
    ] },
  { id: "climate-control", title: "Smart Climate Control", icon: Thermometer, hasQuantity: true, unitLabel: "zone",
    tiers: [
      { label: "Smart thermostat per zone", price: 650 },
      { label: "Multi-zone HVAC + humidity integration", price: 2200 },
    ] },
  { id: "lighting", title: "Smart Lighting", icon: Lightbulb,
    upchargeLabel: "Premium finish upcharge",
    components: [
      { key: "loads", label: "Light load", price: 300 },
      { key: "keypads", label: "Keypad", price: 600 },
      { key: "threeway", label: "Three-way", price: 150 },
    ],
    tiers: [] },
  { id: "shading", title: "Motorized Shading", icon: Blinds, hasQuantity: true, unitLabel: "shade",
    tiers: [
      { label: "Roller shade, standard fabric", price: 950 },
      { label: "Premium roller / roman", price: 1800 },
      { label: "Drapery system with motor", price: 3200 },
    ] },
  { id: "security", title: "Security & Surveillance", icon: Shield,
    tiers: [
      { label: "Starter (4 cameras + doorbell)", price: 3500 },
      { label: "Full 4K system (8+ cameras, NVR, locks)", price: 9500 },
      { label: "Estate-grade security + monitoring integration", price: 25000 },
    ] },
  { id: "networking", title: "Networking & Wi-Fi", icon: Cpu,
    tiers: [
      { label: "Mesh upgrade (3–4 APs)", price: 2500 },
      { label: "Enterprise network (UniFi rack + APs)", price: 6500 },
      { label: "Full structured cabling + 10G backbone", price: 18000 },
    ] },
  { id: "conference", title: "Conference & Office AV", icon: Speaker, hasQuantity: true, unitLabel: "room",
    tiers: [
      { label: "Huddle room (display + soundbar + camera)", price: 4500 },
      { label: "Boardroom video conferencing", price: 18000 },
      { label: "Executive boardroom / video wall", price: 55000 },
    ] },
  { id: "prewire", title: "New Construction Prewire", icon: HardHat,
    tiers: [
      { label: "Basic prewire (under 3,500 sqft)", price: 6500 },
      { label: "Full home prewire (3,500–7,000 sqft)", price: 15000 },
      { label: "Estate prewire (7,000+ sqft)", price: 35000 },
    ] },
  { id: "retrofit", title: "Retrofit & Cable Fishing", icon: Cable,
    tiers: [
      { label: "Single-room retrofit run", price: 450 },
      { label: "Multi-room project (per home)", price: 2800 },
    ] },
  { id: "voice-control", title: "Voice Control", icon: Mic,
    tiers: [
      { label: "Alexa / Google integration setup", price: 750 },
      { label: "Privacy-first local voice control (per room)", price: 2200 },
    ] },
];

function EstimatorPage() {
  const [unlocked, setUnlocked] = useState(false);
  useEffect(() => {
    if (typeof window !== "undefined" && sessionStorage.getItem("apex_estimator_unlocked") === "1" && getStoredPassword()) {
      setUnlocked(true);
    }
  }, []);
  return unlocked ? <Estimator /> : <PasswordGate onUnlock={() => setUnlocked(true)} />;
}

function PasswordGate({ onUnlock }: { onUnlock: () => void }) {
  const verify = useServerFn(verifyEstimatorPassword);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await verify({ data: { password } });
      if (res.ok) {
        setStoredPassword(password);
        onUnlock();
      } else {
        setError("Incorrect password.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="container mx-auto px-6 py-24 flex items-center justify-center min-h-[70vh]">
      <form onSubmit={submit} className="w-full max-w-md rounded-2xl border border-border bg-card p-10 shadow-card">
        <div className="h-12 w-12 rounded-lg bg-gradient-brand flex items-center justify-center shadow-glow">
          <Lock className="h-6 w-6 text-brand-foreground" />
        </div>
        <h1 className="mt-6 font-display text-3xl font-bold">Private estimator</h1>
        <p className="mt-2 text-sm text-muted-foreground">Enter the password provided by Apex AV to access the budget tool.</p>
        <label className="block mt-8 text-sm font-medium">Password</label>
        <input
          type="password"
          aria-label="Password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-2 w-full rounded-md border border-border bg-background px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand"
        />
        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
        <button
          type="submit"
          disabled={loading || !password}
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-gradient-brand px-6 py-3 text-sm font-semibold text-brand-foreground shadow-glow disabled:opacity-50"
        >
          {loading ? "Verifying…" : "Unlock estimator"} <ArrowRight className="h-4 w-4" />
        </button>
      </form>
    </section>
  );
}

type ExtraItem = { id: string; label: string; qty: number; price: number };
type Selection = {
  tier: number | "custom";
  qty: number;
  enabled: boolean;
  customPrice: number;
  customLabel: string;
  componentQtys: Record<string, number>;
  upchargePct: number;
  extras: ExtraItem[];
};

function newExtraId() {
  return (typeof crypto !== "undefined" && crypto.randomUUID?.()) || `${Date.now()}-${Math.random()}`;
}

function Estimator() {
  const { id: loadId } = Route.useSearch();
  const saveFn = useServerFn(saveEstimate);
  const getFn = useServerFn(getEstimate);

  const makeDefaultSel = (): Record<string, Selection> =>
    Object.fromEntries(
      SERVICES.map((s) => [
        s.id,
        {
          tier: 0,
          qty: 1,
          enabled: false,
          customPrice: 0,
          customLabel: "",
          componentQtys: Object.fromEntries((s.components ?? []).map((c) => [c.key, 0])),
          upchargePct: 0,
          extras: [],
        },
      ]),
    );

  const [sel, setSel] = useState<Record<string, Selection>>(makeDefaultSel);
  const [estimateId, setEstimateId] = useState<string | undefined>(loadId);
  const [estimateName, setEstimateName] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientAddress, setClientAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [photoErr, setPhotoErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [loadingEstimate, setLoadingEstimate] = useState(false);
  const blueprintRef = useRef<any>(null);

  useEffect(() => {
    if (!loadId) return;
    const pw = getStoredPassword();
    if (!pw) return;
    setLoadingEstimate(true);
    getFn({ data: { password: pw, id: loadId } })
      .then(({ estimate }) => {
        if (!estimate) return;
        setEstimateId(estimate.id);
        setEstimateName(estimate.name ?? "");
        setClientName(estimate.client_name ?? "");
        setClientEmail(estimate.client_email ?? "");
        setClientPhone(estimate.client_phone ?? "");
        setClientAddress(estimate.client_address ?? "");
        setNotes(estimate.notes ?? "");
        const data = (estimate.data ?? {}) as { sel?: Record<string, Partial<Selection>>; photos?: Photo[]; blueprint?: any };
        blueprintRef.current = data.blueprint ?? null;
        const loadedSel = data.sel;
        if (loadedSel && typeof loadedSel === "object") {
          // Merge with defaults so newly-added services still appear.
          setSel((prev) => {
            const merged: Record<string, Selection> = { ...prev };
            for (const key of Object.keys(loadedSel)) {
              if (merged[key]) merged[key] = { ...merged[key], ...loadedSel[key] } as Selection;
            }
            return merged;
          });
        }
        if (Array.isArray(data.photos)) {
          setPhotos(data.photos.filter((p) => p && typeof p.dataUrl === "string"));
        }
      })
      .catch((err) => setSaveMsg(err instanceof Error ? err.message : "Failed to load."))
      .finally(() => setLoadingEstimate(false));
  }, [loadId, getFn]);

  const priceFor = (s: ServiceItem, v: Selection) =>
    v.tier === "custom" ? Math.max(0, v.customPrice) : s.tiers[v.tier as number]?.price ?? 0;

  const lineTotalFor = (s: ServiceItem, v: Selection) => {
    const extrasTotal = (v.extras ?? []).reduce(
      (sum, e) => sum + Math.max(0, e.price) * Math.max(0, e.qty),
      0,
    );
    if (s.components) {
      const base = s.components.reduce(
        (sum, c) => sum + c.price * Math.max(0, v.componentQtys?.[c.key] ?? 0),
        0,
      );
      return base * (1 + Math.max(0, v.upchargePct) / 100) + extrasTotal;
    }
    return priceFor(s, v) * (s.hasQuantity ? Math.max(1, v.qty) : 1) + extrasTotal;
  };

  const total = useMemo(
    () =>
      SERVICES.reduce((sum, s) => {
        const v = sel[s.id];
        if (!v?.enabled) return sum;
        return sum + lineTotalFor(s, v);
      }, 0),
    [sel],
  );

  const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

  function lockOut() {
    clearStoredPassword();
    window.location.reload();
  }

  async function handleSave() {
    const pw = getStoredPassword();
    if (!pw) {
      setSaveMsg("Session expired. Please unlock again.");
      return;
    }
    const name = estimateName.trim() || (clientName.trim() ? `${clientName.trim()} estimate` : "");
    if (!name) {
      setSaveMsg("Please enter an estimate name or client name before saving.");
      return;
    }
    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await saveFn({
        data: {
          password: pw,
          id: estimateId,
          name,
          client_name: clientName.trim() || null,
          client_email: clientEmail.trim() || null,
          client_phone: clientPhone.trim() || null,
          client_address: clientAddress.trim() || null,
          notes: notes.trim() || null,
          total,
          data: { sel, photos, version: 1, ...(blueprintRef.current ? { blueprint: blueprintRef.current } : {}) },
        },
      });
      setEstimateId(res.id);
      setSaveMsg("Saved.");
    } catch (err) {
      setSaveMsg(err instanceof Error ? err.message : "Failed to save.");
    } finally {
      setSaving(false);
    }
  }

  async function handlePhotoFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setPhotoErr(null);
    setUploadingPhotos(true);
    try {
      const added: Photo[] = [];
      for (const file of Array.from(files)) {
        if (!file.type.startsWith("image/")) continue;
        try {
          const dataUrl = await compressImage(file);
          added.push({
            id: (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`),
            dataUrl,
            caption: "",
            includeInPdf: true,
          });
        } catch {
          // skip
        }
      }
      if (added.length === 0) setPhotoErr("Couldn't read those photos. Try a different file.");
      else setPhotos((p) => [...p, ...added]);
    } finally {
      setUploadingPhotos(false);
    }
  }

  function removePhoto(id: string) {
    setPhotos((p) => p.filter((x) => x.id !== id));
  }

  function updatePhotoCaption(id: string, caption: string) {
    setPhotos((p) => p.map((x) => (x.id === id ? { ...x, caption } : x)));
  }

  function togglePhotoPdf(id: string) {
    setPhotos((p) => p.map((x) => (x.id === id ? { ...x, includeInPdf: x.includeInPdf !== false ? false : true } : x)));
  }


  async function imageToBase64(url: string): Promise<string> {
    const res = await fetch(url);
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  async function downloadPdf() {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const margin = 54;
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    let y = margin;

    // Load logo and add header with company info
    let logoBase64: string | null = null;
    try {
      logoBase64 = await imageToBase64(logoPng);
    } catch {
      // logo won't render if fetch fails
    }

    const logoW = 56;
    const logoH = logoBase64 ? 56 : 0; // approximate square logo
    if (logoBase64) {
      doc.addImage(logoBase64, "PNG", margin, y, logoW, logoH);
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(30, 30, 30);
    doc.text("Apex AV", margin + logoW + 14, y + 18);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(
      "Denver's premier home automation, audio, and video installation specialists.",
      margin + logoW + 14,
      y + 32,
    );
    doc.setFontSize(8.5);
    doc.text(
      "(303) 317-3233   ·   info@apexav.com   ·   apexav.com   ·   Denver, CO",
      margin + logoW + 14,
      y + 46,
    );
    doc.setTextColor(0);

    y = Math.max(y + logoH, y + 58) + 10;
    doc.setDrawColor(210);
    doc.setLineWidth(1);
    doc.line(margin, y, pageW - margin, y);
    y += 18;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(30, 30, 30);
    doc.text("Project Budget Estimate", margin, y);
    y += 20;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(110);
    doc.text(new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }), margin, y);
    y += 18;
    doc.setTextColor(0);

    // Client info block
    const clientLines: { label: string; value: string }[] = [];
    if (clientName.trim()) clientLines.push({ label: "Client", value: clientName.trim() });
    if (clientEmail.trim()) clientLines.push({ label: "Email", value: clientEmail.trim() });
    if (clientPhone.trim()) clientLines.push({ label: "Phone", value: clientPhone.trim() });
    if (clientAddress.trim()) clientLines.push({ label: "Address", value: clientAddress.trim() });
    if (clientLines.length > 0) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(30, 30, 30);
      doc.text("Prepared for", margin, y);
      y += 14;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(60);
      for (const ln of clientLines) {
        const wrapped = doc.splitTextToSize(`${ln.label}: ${ln.value}`, pageW - margin * 2);
        doc.text(wrapped, margin, y);
        y += wrapped.length * 13;
      }
      doc.setTextColor(0);
      y += 6;
    }

    doc.setDrawColor(220);
    doc.setLineWidth(0.5);
    doc.line(margin, y, pageW - margin, y);
    y += 20;

    const ensureSpace = (need: number) => {
      if (y + need > pageH - margin - 40) {
        doc.addPage();
        y = margin;
      }
    };

    const enabled = SERVICES.filter((s) => sel[s.id]?.enabled);
    if (enabled.length === 0) {
      doc.setFontSize(12);
      doc.text("No services selected.", margin, y);
    }

    for (const s of enabled) {
      const v = sel[s.id];
      const lt = lineTotalFor(s, v);
      ensureSpace(60);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.setTextColor(30, 30, 30);
      doc.text(s.title, margin, y);
      doc.text(fmt(lt), pageW - margin, y, { align: "right" });
      y += 16;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(90);

      const detailLines: string[] = [];
      if (s.components) {
        for (const c of s.components) {
          const q = v.componentQtys?.[c.key] ?? 0;
          if (q > 0) detailLines.push(`${c.label} × ${q} @ ${fmt(c.price)}`);
        }
        if (v.upchargePct > 0) {
          detailLines.push(`${s.upchargeLabel ?? "Upcharge"}: +${v.upchargePct}%`);
        }
      } else if (v.tier === "custom") {
        const label = v.customLabel.trim() || "Custom amount";
        const qtyStr = s.hasQuantity ? ` × ${v.qty} ${s.unitLabel}${v.qty > 1 ? "s" : ""}` : "";
        detailLines.push(`${label} — ${fmt(v.customPrice)}${qtyStr}`);
      } else {
        const t = s.tiers[v.tier as number];
        if (t) {
          const qtyStr = s.hasQuantity ? ` × ${v.qty} ${s.unitLabel}${v.qty > 1 ? "s" : ""}` : "";
          detailLines.push(`${t.label} — ${fmt(t.price)}${qtyStr}`);
        }
      }
      for (const ex of v.extras ?? []) {
        const lbl = ex.label.trim() || "Line item";
        detailLines.push(`${lbl} × ${ex.qty} @ ${fmt(ex.price)} = ${fmt(ex.qty * ex.price)}`);
      }

      for (const line of detailLines) {
        const wrapped = doc.splitTextToSize(line, pageW - margin * 2);
        ensureSpace(wrapped.length * 13 + 4);
        doc.text(wrapped, margin + 12, y);
        y += wrapped.length * 13;
      }
      doc.setTextColor(0);
      y += 10;
      doc.setDrawColor(235);
      doc.line(margin, y, pageW - margin, y);
      y += 14;
    }

    ensureSpace(80);
    y += 6;
    doc.setDrawColor(180);
    doc.setLineWidth(1);
    doc.line(margin, y, pageW - margin, y);
    y += 16;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(30, 30, 30);
    doc.text("Estimated Total", margin, y);
    doc.text(fmt(total), pageW - margin, y, { align: "right" });
    y += 26;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(120);
    const disclaimer = doc.splitTextToSize(
      "This is a preliminary budget estimate, not a final price. Final scope, materials, programming hours, and site conditions will be confirmed in your fixed-price proposal from Apex AV.",
      pageW - margin * 2,
    );
    doc.text(disclaimer, margin, y);
    y += disclaimer.length * 11 + 14;

    // Photos
    if (photos.filter((p) => p.includeInPdf !== false).length > 0) {
      doc.addPage();
      y = margin;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.setTextColor(30, 30, 30);
      doc.text("Project Photos", margin, y);
      y += 22;

      const gap = 14;
      const colW = (pageW - margin * 2 - gap) / 2;
      const maxImgH = 220;
      let col = 0;
      let rowTopY = y;
      let rowMaxBottom = y;

      for (const photo of photos.filter((p) => p.includeInPdf !== false)) {
        try {
          const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const i = new Image();
            i.onload = () => resolve(i);
            i.onerror = reject;
            i.src = photo.dataUrl;
          });
          const ratio = img.width / img.height;
          let drawW = colW;
          let drawH = drawW / ratio;
          if (drawH > maxImgH) {
            drawH = maxImgH;
            drawW = drawH * ratio;
          }
          const captionLines = photo.caption.trim()
            ? doc.splitTextToSize(photo.caption.trim(), colW)
            : [];
          const captionH = captionLines.length ? captionLines.length * 11 + 4 : 0;
          const blockH = drawH + captionH;

          if (col === 0 && rowTopY + blockH > pageH - margin - 40) {
            doc.addPage();
            y = margin;
            rowTopY = y;
            rowMaxBottom = y;
          }

          const x = margin + col * (colW + gap);
          doc.addImage(photo.dataUrl, "JPEG", x, rowTopY, drawW, drawH);
          if (captionLines.length) {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.setTextColor(90);
            doc.text(captionLines, x, rowTopY + drawH + 12);
          }
          rowMaxBottom = Math.max(rowMaxBottom, rowTopY + blockH);
          col++;
          if (col >= 2) {
            col = 0;
            y = rowMaxBottom + 16;
            rowTopY = y;
          }
        } catch {
          // skip broken image
        }
      }
    }



    // Footer on every page
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(140);
      doc.text(
        `© ${new Date().getFullYear()} Apex AV · (303) 317-3233 · info@apexav.com · apexav.com · Denver, CO`,
        pageW / 2,
        pageH - 24,
        { align: "center" },
      );
    }

    doc.save(`apex-av-estimate-${new Date().toISOString().slice(0, 10)}.pdf`);
  }


  return (
    <section className="container mx-auto px-6 pt-16 pb-32">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold text-brand-text uppercase tracking-wider">Private tool</p>
          <h1 className="mt-3 font-display text-5xl md:text-6xl font-bold">Project budget estimator</h1>
          <p className="mt-4 text-muted-foreground">
            Toggle the services you'd like included, pick a tier, and adjust quantities. Estimates are starting points — your final scope is set during your consultation.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Link
            to="/estimator/saved"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground"
          >
            <FolderOpen className="h-3.5 w-3.5" /> Saved estimates
          </Link>
          <button onClick={lockOut} className="text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground">
            Lock
          </button>
        </div>
      </div>

      {loadingEstimate && (
        <p className="mt-6 text-sm text-muted-foreground">Loading saved estimate…</p>
      )}

      <div className="mt-12 grid gap-8 lg:grid-cols-[1fr_360px]">
        <div className="grid gap-4">
          {SERVICES.map((s) => {
            const v = sel[s.id];
            const lineTotal = lineTotalFor(s, v);
            return (
              <div
                key={s.id}
                className={`rounded-xl border bg-card p-6 transition ${v.enabled ? "border-brand/50 shadow-card" : "border-border"}`}
              >
                <div className="flex items-start gap-4">
                  <div className="h-11 w-11 rounded-lg bg-gradient-brand flex items-center justify-center shadow-glow shrink-0">
                    <s.icon className="h-5 w-5 text-brand-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-display text-lg font-bold">{s.title}</h3>
                      <label className="inline-flex items-center gap-2 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={v.enabled}
                          onChange={(e) => setSel((p) => ({ ...p, [s.id]: { ...p[s.id], enabled: e.target.checked } }))}
                          className="h-4 w-4 accent-brand"
                        />
                        Include
                      </label>
                    </div>
                    {v.enabled && (
                      <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto] items-end">
                        {s.components ? (
                          <div className="sm:col-span-2 grid gap-3">
                            <div className="grid gap-3 sm:grid-cols-3">
                              {s.components.map((c) => (
                                <div key={c.key}>
                                  <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                    {c.label}s ({fmt(c.price)} ea)
                                  </label>
                                  <NumberInputRaw
                                    min={0}
                                    value={v.componentQtys?.[c.key] ?? 0}
                                    onValueChange={(n) =>
                                      setSel((p) => ({
                                        ...p,
                                        [s.id]: {
                                          ...p[s.id],
                                          componentQtys: {
                                            ...p[s.id].componentQtys,
                                            [c.key]: Math.max(0, n),
                                          },
                                        },
                                      }))
                                    }
                                    className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                                  />
                                </div>
                              ))}
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                {s.upchargeLabel ?? "Custom upcharge"} (%)
                              </label>
                              <NumberInputRaw
                                min={0}
                                step={1}
                                value={v.upchargePct ?? 0}
                                placeholder="0"
                                onValueChange={(n) =>
                                  setSel((p) => ({
                                    ...p,
                                    [s.id]: { ...p[s.id], upchargePct: Math.max(0, n) },
                                  }))
                                }
                                className="mt-1.5 w-32 rounded-md border border-border bg-background px-3 py-2 text-sm"
                              />
                            </div>
                          </div>
                        ) : (
                          <>
                            <div>
                              <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider">Tier</label>
                              <div className="relative mt-1.5">
                                <select
                                  value={String(v.tier)}
                                  onChange={(e) => setSel((p) => ({ ...p, [s.id]: { ...p[s.id], tier: e.target.value === "custom" ? "custom" : Number(e.target.value) } }))}
                                  className="w-full appearance-none rounded-md border border-border bg-background px-3 py-2 pr-9 text-sm"
                                >
                                  {s.tiers.map((t, i) => (
                                    <option key={i} value={i}>
                                      {t.label} — {fmt(t.price)}{s.hasQuantity ? ` / ${s.unitLabel}` : ""}
                                    </option>
                                  ))}
                                  <option value="custom">Custom amount…</option>
                                </select>
                                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                              </div>
                              {v.tier === "custom" && (
                                <div className="mt-3">
                                  <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                    Custom amount{s.hasQuantity ? ` (per ${s.unitLabel})` : ""}
                                  </label>
                                  <div className="mt-1.5 flex items-center gap-2">
                                    <span className="text-muted-foreground">$</span>
                                    <NumberInputRaw
                                      min={0}
                                      step={50}
                                      value={v.customPrice ?? 0}
                                      placeholder="0"
                                      onValueChange={(n) => setSel((p) => ({ ...p, [s.id]: { ...p[s.id], customPrice: Math.max(0, n) } }))}
                                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                                    />
                                  </div>
                                  <label className="mt-3 block text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                    Description
                                  </label>
                                  <input
                                    type="text"
                                    value={v.customLabel}
                                    placeholder="Describe this custom line item"
                                    onChange={(e) => setSel((p) => ({ ...p, [s.id]: { ...p[s.id], customLabel: e.target.value } }))}
                                    className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                                  />
                                </div>
                              )}
                            </div>
                            {s.hasQuantity && (
                              <div>
                                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                  {s.unitLabel}s
                                </label>
                                <NumberInputRaw
                                  min={1}
                                  value={v.qty}
                                  onValueChange={(n) => setSel((p) => ({ ...p, [s.id]: { ...p[s.id], qty: Math.max(1, n) } }))}
                                  className="mt-1.5 w-24 rounded-md border border-border bg-background px-3 py-2 text-sm"
                                />
                              </div>
                            )}
                          </>
                        )}
                        <div className="sm:col-span-2 border-t border-border pt-3 mt-1">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs uppercase tracking-wider text-muted-foreground">Additional line items</span>
                            <button
                              type="button"
                              onClick={() => setSel((p) => ({
                                ...p,
                                [s.id]: {
                                  ...p[s.id],
                                  extras: [
                                    ...(p[s.id].extras ?? []),
                                    { id: newExtraId(), label: "", qty: 1, price: 0 },
                                  ],
                                },
                              }))}
                              className="text-xs inline-flex items-center gap-1 rounded-md border border-dashed border-border px-2 py-1 hover:bg-muted"
                            >
                              + Add line item
                            </button>
                          </div>
                          {(v.extras ?? []).length > 0 && (
                            <div className="space-y-2">
                              {(v.extras ?? []).map((ex, i) => (
                                <div key={ex.id} className="grid grid-cols-[1fr_70px_110px_auto] gap-2 items-center">
                                  <input
                                    type="text"
                                    value={ex.label}
                                    placeholder="Description"
                                    onChange={(e) => setSel((p) => ({
                                      ...p,
                                      [s.id]: {
                                        ...p[s.id],
                                        extras: (p[s.id].extras ?? []).map((x, j) => j === i ? { ...x, label: e.target.value } : x),
                                      },
                                    }))}
                                    className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                                  />
                                  <NumberInputRaw
                                    min={0}
                                    value={ex.qty}
                                    onValueChange={(n) => setSel((p) => ({
                                      ...p,
                                      [s.id]: {
                                        ...p[s.id],
                                        extras: (p[s.id].extras ?? []).map((x, j) => j === i ? { ...x, qty: Math.max(0, n) } : x),
                                      },
                                    }))}
                                    aria-label="Quantity"
                                    className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                                  />
                                  <div className="flex items-center gap-1">
                                    <span className="text-muted-foreground text-sm">$</span>
                                    <NumberInputRaw
                                      min={0}
                                      step={1}
                                      value={ex.price ?? 0}
                                      placeholder="0"
                                      onValueChange={(n) => setSel((p) => ({
                                        ...p,
                                        [s.id]: {
                                          ...p[s.id],
                                          extras: (p[s.id].extras ?? []).map((x, j) => j === i ? { ...x, price: Math.max(0, n) } : x),
                                        },
                                      }))}
                                      aria-label="Price"
                                      className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setSel((p) => ({
                                      ...p,
                                      [s.id]: {
                                        ...p[s.id],
                                        extras: (p[s.id].extras ?? []).filter((_, j) => j !== i),
                                      },
                                    }))}
                                    aria-label="Remove line item"
                                    className="text-muted-foreground hover:text-destructive p-1"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="sm:col-span-2 flex items-center justify-between border-t border-border pt-3 mt-1">
                          <span className="text-xs uppercase tracking-wider text-muted-foreground">Line subtotal</span>
                          <span className="font-display text-lg font-bold">{fmt(lineTotal)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          <div className="rounded-xl border border-border bg-card p-6">
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-lg bg-gradient-brand flex items-center justify-center shadow-glow shrink-0">
                <Camera className="h-5 w-5 text-brand-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-lg font-bold">Project photos</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Attach photos of the spaces, equipment, or wiring. Included in the saved estimate and the PDF.
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-muted">
                    <ImagePlus className="h-4 w-4" />
                    Add from library
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        handlePhotoFiles(e.target.files);
                        e.target.value = "";
                      }}
                    />
                  </label>
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-muted">
                    <Camera className="h-4 w-4" />
                    Take photo
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={(e) => {
                        handlePhotoFiles(e.target.files);
                        e.target.value = "";
                      }}
                    />
                  </label>
                  {uploadingPhotos && (
                    <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Processing…
                    </span>
                  )}
                </div>
                {photoErr && <p className="mt-2 text-xs text-destructive">{photoErr}</p>}
                {photos.length > 0 && (
                  <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {photos.map((photo) => (
                      <div key={photo.id} className="group relative rounded-lg border border-border overflow-hidden bg-muted">
                        <div className="relative aspect-square">
                          <img src={photo.dataUrl} alt={photo.caption || "Client project reference photo"} className="h-full w-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removePhoto(photo.id)}
                            aria-label="Remove photo"
                            className="absolute right-1.5 top-1.5 inline-flex h-7 w-7 items-center justify-center rounded-full bg-background/90 text-foreground shadow-sm hover:bg-background"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <label className="flex items-center gap-2 border-t border-border bg-card px-2 py-1.5 text-xs cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={photo.includeInPdf !== false}
                            onChange={() => togglePhotoPdf(photo.id)}
                            className="h-3.5 w-3.5 accent-brand"
                          />
                          Include on PDF
                        </label>
                        <input
                          type="text"
                          value={photo.caption}
                          onChange={(e) => updatePhotoCaption(photo.id, e.target.value)}
                          placeholder="Caption (optional)"
                          className="w-full border-t border-border bg-card px-2 py-1.5 text-xs focus:outline-none"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>


        <aside className="lg:sticky lg:top-24 self-start rounded-2xl border border-border bg-card p-8 shadow-card">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Estimated total</p>
          <p className="mt-2 font-display text-5xl font-bold">{fmt(total)}</p>
          <p className="mt-3 text-xs text-muted-foreground">
            This is a preliminary budget estimate, not a final price. Final scope, materials, programming hours, and site conditions will be confirmed in your fixed-price proposal.
          </p>
          <div className="mt-6 space-y-2 text-sm">
            {SERVICES.filter((s) => sel[s.id]?.enabled).map((s) => {
              const v = sel[s.id];
              const lt = lineTotalFor(s, v);
              const extras = v.extras ?? [];
              return (
                <div key={s.id} className="space-y-1">
                  <div className="flex justify-between gap-3 text-muted-foreground">
                    <span className="truncate">
                      {s.title}
                      {v.tier === "custom" && v.customLabel.trim() ? ` — ${v.customLabel.trim()}` : ""}
                      {s.hasQuantity && v.qty > 1 ? ` ×${v.qty}` : ""}
                    </span>
                    <span className="text-foreground font-medium shrink-0">{fmt(lt)}</span>
                  </div>
                  {extras.map((ex) => (
                    <div key={ex.id} className="flex justify-between gap-3 text-muted-foreground text-xs pl-3">
                      <span className="truncate">↳ {ex.label || "Line item"}{ex.qty > 1 ? ` ×${ex.qty}` : ""}</span>
                      <span className="shrink-0">{fmt(ex.price * ex.qty)}</span>
                    </div>
                  ))}
                </div>
              );
            })}
            {SERVICES.every((s) => !sel[s.id]?.enabled) && (
              <p className="text-muted-foreground text-sm">No services selected yet.</p>
            )}
          </div>

          <div className="mt-6 space-y-3 border-t border-border pt-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Save this estimate
            </p>
            <input
              type="text"
              value={estimateName}
              onChange={(e) => setEstimateName(e.target.value)}
              placeholder="Estimate name"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
            <input
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Client name (optional)"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
            <input
              type="email"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              placeholder="Client email (optional)"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
            <input
              type="tel"
              value={clientPhone}
              onChange={(e) => setClientPhone(e.target.value)}
              placeholder="Client phone (optional)"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
            <input
              type="text"
              value={clientAddress}
              onChange={(e) => setClientAddress(e.target.value)}
              placeholder="Client address (optional)"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes (optional)"
              rows={2}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-foreground px-6 py-2.5 text-sm font-semibold text-background hover:opacity-90 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {saving ? "Saving…" : estimateId ? "Update saved estimate" : "Save estimate"}
            </button>
            {saveMsg && <p className="text-xs text-muted-foreground">{saveMsg}</p>}
          </div>

          <button
            onClick={downloadPdf}
            disabled={total === 0}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md border border-border bg-background px-6 py-3 text-sm font-semibold hover:bg-muted disabled:opacity-50"
          >
            <Download className="h-4 w-4" /> Download estimate (PDF)
          </button>
          <Link
            to="/contact"
            className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-md bg-gradient-brand px-6 py-3 text-sm font-semibold text-brand-foreground shadow-glow"
          >
            Book consultation <ArrowRight className="h-4 w-4" />
          </Link>
        </aside>
      </div>
    </section>
  );
}
